import {useEffect, useMemo} from 'react';

import {Period} from '@deviceinsight/ng-ui-components';

import {createFooterLabel, UPDATE_WIDGET_INTERVAL} from '../common/kpiUtils';
import ChartWidgetContent from '../common/components/ChartWidgetContent';
import useSingleValueColumnChart from './useSingleValueColumnChart';
import {AggregationSettings} from '../common/types';
import {KpiSingleValueColumnWidgetConfigSettings, KpiSingleValueColumnWidgetProps} from './types';
import useMultipleKpiValues from '../common/hooks/useMultipleKpiValues';
import {OutdatedConfigError} from '../common/components/kpiWidgetErrors';
import {WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';

const createAggregationSettings = (
	context: WidgetProps['context'],
	settings: KpiSingleValueColumnWidgetConfigSettings,
	dashboardTimePeriod: Period | null | undefined
): AggregationSettings => {
	const urn = context.type === 'asset' ? context.asset.urn : settings.assetUrn;
	const items =
		settings.kpiName && urn
			? [
					{
						kpiName: settings.kpiName,
						assetUrn: urn
					}
			  ]
			: [];
	return {
		items,
		aggregationType: settings.aggregationType,
		query: {
			periodTimeframe: dashboardTimePeriod ? 'CUSTOM' : settings.periodTimeframe,
			periodType: settings.periodType,
			periodCount: settings.periodCount,
			customPeriodFrom: dashboardTimePeriod ? dashboardTimePeriod.tsStart : settings.customPeriodFrom,
			customPeriodTo: dashboardTimePeriod ? dashboardTimePeriod.tsEnd : settings.customPeriodTo,
			workingShiftEnabled: settings.workingShiftEnabled,
			workingShiftDays: settings.workingShiftDays,
			workingShiftTimeFrom: settings.workingShiftTimeFrom,
			workingShiftTimeTo: settings.workingShiftTimeTo
		}
	};
};

const KpiSingleValueColumnWidget = ({
	context,
	config: {
		title,
		key,
		settings,
		settings: {kpiName, periodTimeframe}
	},
	editMode,
	dashboardTime,
	dashboardPeriod
}: KpiSingleValueColumnWidgetProps) => {
	const aggregationSettings = useMemo(
		() => createAggregationSettings(context, settings, dashboardPeriod),
		[context, settings, dashboardPeriod]
	);
	const {values, fetching, errorMessage, lastUpdate, reload} = useMultipleKpiValues(
		context,
		aggregationSettings,
		key,
		dashboardTime,
		editMode
	);
	const setChartNode = useSingleValueColumnChart(values?.[0]?.value, settings, editMode);
	useEffect(() => {
		const timeoutId = setTimeout(reload, UPDATE_WIDGET_INTERVAL);
		return () => clearTimeout(timeoutId);
	}, [reload, lastUpdate]);

	if (periodTimeframe) {
		const footerLabel = createFooterLabel(aggregationSettings, dashboardTime);
		return (
			<ChartWidgetContent
				loading={fetching}
				title={title}
				kpiErrorMessage={errorMessage}
				kpiNames={kpiName || ''}
				containerRef={setChartNode}
				footerLabel={footerLabel}
				dashboardTime={dashboardTime}
				editMode={editMode}
				lastUpdate={lastUpdate}
			/>
		);
	} else {
		return <OutdatedConfigError />;
	}
};

export default KpiSingleValueColumnWidget;
